import { ChangePasswordRequest } from '@stayafrica/contracts';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  FormField,
  Notice,
  OtpInput,
  PageHeader,
  PasswordInput,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
  toast,
} from '@stayafrica/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { KeyRound, Monitor, ShieldCheck, User as UserIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import {
  authKeys,
  changePassword,
  disableMfa,
  enableMfa,
  revokeOtherSessions,
  sessionsQueryOptions,
  startMfaSetup,
} from '@/features/auth/queries';
import { useSession } from '@/features/auth/session';
import { NotificationPreferencesCard } from '@/features/notifications/NotificationPreferencesCard';
import { errorMessage } from '@/lib/errors';
import { formatRelative } from '@/lib/format';

export const Route = createFileRoute('/_app/me')({ component: ProfilePage });

type PasswordFormValues = z.infer<typeof ChangePasswordRequest>;

function ProfilePage() {
  const { t, i18n } = useTranslation(['profile', 'common', 'errors', 'users']);
  const { user } = useSession();
  const queryClient = useQueryClient();

  const sessionsQuery = useQuery(sessionsQueryOptions());

  const [mfaSetup, setMfaSetup] = useState<Awaited<ReturnType<typeof startMfaSetup>> | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(ChangePasswordRequest),
    defaultValues: { currentPassword: '', newPassword: '' },
    mode: 'onBlur',
  });

  const changePasswordMutation = useMutation({
    mutationFn: (values: PasswordFormValues) => changePassword(values),
    onSuccess: () => {
      toast.success(t('profile:toasts.passwordChanged'));
      passwordForm.reset();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const setupMutation = useMutation({
    mutationFn: startMfaSetup,
    onSuccess: (data) => setMfaSetup(data),
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const enableMutation = useMutation({
    mutationFn: (code: string) => enableMfa({ code }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authKeys.me });
      setMfaSetup(null);
      setMfaCode('');
      toast.success(t('profile:toasts.mfaEnabled'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const disableMutation = useMutation({
    mutationFn: () => disableMfa({ password: disablePassword, code: disableCode }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authKeys.me });
      setDisablePassword('');
      setDisableCode('');
      toast.success(t('profile:toasts.mfaDisabled'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const revokeOthersMutation = useMutation({
    mutationFn: revokeOtherSessions,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authKeys.sessions });
      toast.success(t('profile:toasts.othersRevoked'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const sessions = sessionsQuery.data ?? [];

  return (
    <Container width="narrow">
      <PageHeader title={t('profile:title')} subtitle={t('profile:subtitle')} />

      <div className="stack">
        <Card>
          <CardHeader
            title={t('profile:identityCard')}
            icon={<UserIcon className="ico ico-16" aria-hidden="true" />}
          />
          <CardBody>
            <div className="row" style={{ gap: 16, alignItems: 'flex-start' }}>
              <Avatar name={user.fullName} size="xl" tone="grad" />
              <dl className="dl dl--lines" style={{ flex: 1 }}>
                <dt>{t('common:fields.fullName')}</dt>
                <dd>{user.fullName}</dd>
                <dt>{t('common:fields.email')}</dt>
                <dd>{user.email}</dd>
                <dt>{t('common:fields.language')}</dt>
                <dd>{t(`common:languages.${user.locale}`)}</dd>
                <dt>{t('users:columns.roles')}</dt>
                <dd>
                  <span
                    className="row"
                    style={{ gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}
                  >
                    {user.roles.map((role) => (
                      <Badge key={role.id} tone="violet">
                        {role.name}
                      </Badge>
                    ))}
                  </span>
                </dd>
              </dl>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={t('profile:passwordCard')}
            icon={<KeyRound className="ico ico-16" aria-hidden="true" />}
            iconTone="amber"
          />
          <CardBody>
            <form
              className="stack"
              onSubmit={(event) => {
                event.preventDefault();
                void passwordForm.handleSubmit((values) => changePasswordMutation.mutate(values))(
                  event,
                );
              }}
              noValidate
            >
              <div className="field-row">
                <FormField
                  label={t('profile:currentPassword')}
                  error={
                    passwordForm.formState.errors.currentPassword
                      ? t('common:validation.required')
                      : undefined
                  }
                  required
                  requiredLabel={t('common:fields.required')}
                >
                  {(field) => (
                    <PasswordInput
                      id={field.id}
                      autoComplete="current-password"
                      invalid={field.invalid}
                      aria-describedby={field.describedBy}
                      showLabel={t('common:actions.showPassword')}
                      hideLabel={t('common:actions.hidePassword')}
                      {...passwordForm.register('currentPassword')}
                    />
                  )}
                </FormField>

                <FormField
                  label={t('profile:newPassword')}
                  hint={t('common:validation.passwordPolicy')}
                  error={
                    passwordForm.formState.errors.newPassword
                      ? t('common:validation.passwordPolicy')
                      : undefined
                  }
                  required
                  requiredLabel={t('common:fields.required')}
                >
                  {(field) => (
                    <PasswordInput
                      id={field.id}
                      autoComplete="new-password"
                      invalid={field.invalid}
                      aria-describedby={field.describedBy}
                      showLabel={t('common:actions.showPassword')}
                      hideLabel={t('common:actions.hidePassword')}
                      {...passwordForm.register('newPassword')}
                    />
                  )}
                </FormField>
              </div>
              <div className="row">
                <span className="spacer" />
                <Button type="submit" variant="primary" loading={changePasswordMutation.isPending}>
                  {t('profile:changePassword')}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={t('profile:mfaCard')}
            icon={<ShieldCheck className="ico ico-16" aria-hidden="true" />}
            iconTone="green"
            actions={
              <Badge tone={user.mfaEnabled ? 'green' : 'amber'} dot>
                {user.mfaEnabled ? t('profile:mfaEnabled') : t('profile:mfaDisabled')}
              </Badge>
            }
          />
          <CardBody>
            <p className="muted">
              {user.mfaEnabled ? t('profile:mfaEnabledBody') : t('profile:mfaDisabledBody')}
            </p>

            {user.mfaEnabled ? (
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="danger">{t('profile:disableMfa')}</Button>
                </DialogTrigger>
                <DialogContent
                  title={t('profile:mfaDisableTitle')}
                  description={t('profile:mfaDisableBody')}
                  closeLabel={t('common:actions.close')}
                  icon={<ShieldCheck className="ico ico-16" aria-hidden="true" />}
                  iconTone="red"
                  footer={
                    <>
                      <DialogClose asChild>
                        <Button variant="ghost">{t('common:actions.cancel')}</Button>
                      </DialogClose>
                      <Button
                        variant="danger"
                        loading={disableMutation.isPending}
                        disabled={disableCode.length !== 6 || disablePassword.length === 0}
                        onClick={() => disableMutation.mutate()}
                      >
                        {t('profile:disableMfa')}
                      </Button>
                    </>
                  }
                >
                  <div className="stack">
                    <FormField
                      label={t('profile:currentPassword')}
                      required
                      requiredLabel={t('common:fields.required')}
                    >
                      {(field) => (
                        <PasswordInput
                          id={field.id}
                          value={disablePassword}
                          onChange={(event) => setDisablePassword(event.target.value)}
                          autoComplete="current-password"
                          showLabel={t('common:actions.showPassword')}
                          hideLabel={t('common:actions.hidePassword')}
                        />
                      )}
                    </FormField>
                    <div className="field">
                      <span className="label">{t('profile:mfaSetupStep2')}</span>
                      <OtpInput
                        value={disableCode}
                        onChange={setDisableCode}
                        label={t('profile:mfaCard')}
                      />
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            ) : mfaSetup ? (
              <div className="stack">
                <p className="strong mb-0">{t('profile:mfaSetupStep1')}</p>
                <div
                  className="row"
                  style={{ alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}
                >
                  <span className="qr-frame">
                    <img src={mfaSetup.qrCodeDataUrl} alt="" />
                  </span>
                  <div className="stack stack--sm">
                    <span className="hint">{t('profile:mfaSecret')}</span>
                    <code className="mono">{mfaSetup.secret}</code>
                    <span className="label mt-3">{t('profile:mfaSetupStep2')}</span>
                    <OtpInput
                      value={mfaCode}
                      onChange={setMfaCode}
                      onComplete={(code) => enableMutation.mutate(code)}
                      label={t('profile:mfaCard')}
                    />
                  </div>
                </div>
                <div className="row">
                  <span className="spacer" />
                  <Button variant="ghost" onClick={() => setMfaSetup(null)}>
                    {t('common:actions.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    loading={enableMutation.isPending}
                    disabled={mfaCode.length !== 6}
                    onClick={() => enableMutation.mutate(mfaCode)}
                  >
                    {t('profile:enableMfa')}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="primary"
                loading={setupMutation.isPending}
                onClick={() => setupMutation.mutate()}
              >
                {t('profile:enableMfa')}
              </Button>
            )}
          </CardBody>
        </Card>

        <NotificationPreferencesCard />

        <Card>
          <CardHeader
            title={t('profile:sessionsCard')}
            icon={<Monitor className="ico ico-16" aria-hidden="true" />}
            iconTone="blue"
            actions={
              sessions.length > 1 ? (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={revokeOthersMutation.isPending}
                  onClick={() => revokeOthersMutation.mutate()}
                >
                  {t('profile:revokeOthers')}
                </Button>
              ) : null
            }
          />
          {sessionsQuery.isError ? (
            <CardBody>
              <Notice tone="danger">{errorMessage(sessionsQuery.error, t)}</Notice>
            </CardBody>
          ) : (
            <TableWrap>
              <Table compact>
                <thead>
                  <tr>
                    <Th>{t('profile:device')}</Th>
                    <Th>{t('profile:ip')}</Th>
                    <Th>{t('profile:lastSeen')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session) => (
                    <Tr key={session.id}>
                      <Td strong>
                        {session.userAgent ?? '—'}
                        {session.current ? (
                          <Badge tone="violet" style={{ marginLeft: 8 }}>
                            {t('profile:current')}
                          </Badge>
                        ) : null}
                      </Td>
                      <Td numeric className="t-13">
                        {session.ip ?? '—'}
                      </Td>
                      <Td numeric className="t-13">
                        {formatRelative(session.lastSeenAt, i18n.language)}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </Card>
      </div>
    </Container>
  );
}
